/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector('form');

    if (this.form) {
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleInvalid = this.handleInvalid.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.form.addEventListener('submit', this.handleSubmit, true);
      this.form.addEventListener('invalid', this.handleInvalid, true);
      this.form.addEventListener('input', this.handleInput, true);
    }
  }

  disconnectedCallback() {
    if (this.form && this.handleSubmit) {
      this.form.removeEventListener('submit', this.handleSubmit, true);
    }
    if (this.form && this.handleInvalid) {
      this.form.removeEventListener('invalid', this.handleInvalid, true);
    }
    if (this.form && this.handleInput) {
      this.form.removeEventListener('input', this.handleInput, true);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  getValidationMessage(input) {
    const value = input.value.trim();
    if (!value) {
      return 'Please fill out this field.';
    }
    if (input.matches('[data-email-signup-email]')) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) {
        return 'Please enter a valid email address.';
      }
    }
    return '';
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  setFieldError(input, message) {
    const fieldWrapper = input.closest('.email-signup__field');
    const errorEl = fieldWrapper
      ? fieldWrapper.querySelector('[data-email-signup-error]')
      : null;

    input.classList.toggle('is-invalid', Boolean(message));
    if (message) {
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('aria-invalid');
    }

    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.toggle('is-visible', Boolean(message));
    }
  }

  /**
   * @param {Event} event
   */
  handleInvalid(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target || typeof target.matches !== 'function') return;
    if (!target.matches('[data-email-signup-required]')) return;

    const message = this.getValidationMessage(target);
    if (message) {
      this.setFieldError(target, message);
    }
  }

  /**
   * @param {Event} event
   */
  handleInput(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target || typeof target.matches !== 'function') return;
    if (!target.matches('[data-email-signup-required]')) return;

    const message = this.getValidationMessage(target);
    if (!message) {
      this.setFieldError(target, '');
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    if (!this.form) return;

    const requiredFields = Array.from(
      this.form.querySelectorAll('[data-email-signup-required]')
    );

    let hasError = false;
    /** @type {HTMLInputElement | null} */
    let firstInvalid = null;

    requiredFields.forEach((field) => {
      const input = /** @type {HTMLInputElement} */ (field);
      const message = this.getValidationMessage(input);
      this.setFieldError(input, message);
      if (message) {
        hasError = true;
        if (!firstInvalid) firstInvalid = input;
      }
    });

    if (hasError) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (firstInvalid) {
        /** @type {HTMLInputElement} */ (firstInvalid).focus();
      }
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
